import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./ooraTomtomTraffic.css";
import { getOoraTomtomTrafficList } from "../../Redux/Actions/ooraTrafficTomActions";
import {
    formatDate, formatSlot, formatDuration, formatDistance,
    kindOf, KIND_LABEL, LEVELS, SLOTS, speedText, speedLevel, routeText,
} from "./ooraTomtomTrafficHelpers";
import OoraSideNav from "../Oora Sidebar/ooraSideNav";
import "../Oora Sidebar/ooraSideNav.css";

const LIMIT = 10;
const KIND_TABS = [
    { key: "", label: "All" },
    { key: "INCIDENT", label: "Incidents" },
    { key: "ROAD", label: "Roads" },
];

const ListOoraTomtomTraffic = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { ooraTomtomTrafficList: listRes, listLoading } = useSelector((s) => s.ooraTomtomTraffic || {});

    const rows = listRes?.data?.list || [];
    const total = listRes?.data?.total || 0;
    const pages = Math.max(1, Math.ceil(total / LIMIT));

    const [page, setPage] = useState(1);
    const [typed, setTyped] = useState("");
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState({ kind: "", date_label: "", slot: "", traffic_level: "" });

    // one effect loads the list whenever page, search or a filter changes
    useEffect(() => {
        const body = { page, limit: LIMIT };
        if (search) body.search = search;
        Object.entries(filters).forEach(([k, v]) => { if (v) body[k] = v; });
        dispatch(getOoraTomtomTrafficList(body));
    }, [dispatch, page, search, filters]);

    // debounce the search box
    useEffect(() => {
        const t = setTimeout(() => { setPage(1); setSearch(typed.trim()); }, 400);
        return () => clearTimeout(t);
    }, [typed]);

    const setFilter = (key) => (e) => {
        const value = e.target.value;
        setPage(1);
        setFilters((f) => ({ ...f, [key]: value }));
    };

    return (
        <div className="oora-layout">
            <OoraSideNav />
            <div className="oora-layout-main">
                <div className="ott-page">
                    <div className="ott-head">
                        <div>
                            <h1>TomTom Traffic</h1>
                            <p>Jams and road travel times in Noida, as read from TomTom</p>
                        </div>
                    </div>

                    <div className="ott-panel">
                        <div className="ott-filters">
                            <div className="ott-tabs" role="tablist">
                                {KIND_TABS.map((t) => (
                                    <button
                                        key={t.key || "all"}
                                        type="button"
                                        role="tab"
                                        aria-selected={filters.kind === t.key}
                                        className={`ott-tab ${filters.kind === t.key ? "is-on" : ""}`}
                                        onClick={() => { setPage(1); setFilters((f) => ({ ...f, kind: t.key })); }}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>

                            <input
                                className="ott-field ott-search"
                                type="search"
                                placeholder="Search road, from or to"
                                value={typed}
                                onChange={(e) => setTyped(e.target.value)}
                            />
                            <input className="ott-field" type="date" aria-label="Date" value={filters.date_label} onChange={setFilter("date_label")} />
                            <select className="ott-field" aria-label="Slot" value={filters.slot} onChange={setFilter("slot")}>
                                <option value="">All slots</option>
                                {SLOTS.map((s) => <option key={s} value={s}>{formatSlot(s)}</option>)}
                            </select>
                            <select className="ott-field" aria-label="Traffic level" value={filters.traffic_level} onChange={setFilter("traffic_level")}>
                                <option value="">All levels</option>
                                {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                            </select>

                            <span className="ott-total">{total} record{total === 1 ? "" : "s"}</span>
                        </div>

                        <div className="ott-scroll">
                            <table className="ott-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Road</th>
                                        <th>Kind</th>
                                        <th>Route</th>
                                        <th>Date</th>
                                        <th>Slot (IST)</th>
                                        <th>Traffic</th>
                                        <th>Type</th>
                                        <th>Jam length</th>
                                        <th>Delay</th>
                                        <th aria-label="Actions" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {listLoading ? (
                                        <tr><td colSpan={11} className="ott-empty">Loading...</td></tr>
                                    ) : rows.length === 0 ? (
                                        <tr><td colSpan={11} className="ott-empty">No TomTom traffic matches these filters</td></tr>
                                    ) : (
                                        rows.map((r, i) => (
                                            <tr key={r._id}>
                                                <td className="ott-num">{(page - 1) * LIMIT + i + 1}</td>
                                                <td className="ott-strong">{r.road_name || "Unnamed road"}</td>
                                                <td><span className="ott-badge ott-kind">{KIND_LABEL[kindOf(r)]}</span></td>
                                                <td>{routeText(r)}</td>
                                                <td>{formatDate(r.date_label)}</td>
                                                <td>{formatSlot(r.slot)}</td>
                                                <td><span className={`ott-badge ott-lvl-${r.traffic_level}`}>{r.traffic_level}</span></td>
                                                <td>
                                                    {r.speed
                                                        ? <span className={`ott-badge ott-lvl-${speedLevel(r.speed)}`}>{speedText(r.speed)}</span>
                                                        : "-"}
                                                </td>
                                                <td>{formatDistance(r.jam_length_meters)}</td>
                                                <td>{formatDuration(r.delay_sec)}</td>
                                                <td>
                                                    <button type="button" className="ott-icon-btn" title="View" aria-label="View" onClick={() => navigate(`/oora_tomtom_traffic/${r._id}`)}>
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                            <circle cx="12" cy="12" r="3" />
                                                        </svg>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="ott-pager">
                            <span>Page {page} of {pages}</span>
                            <div>
                                <button type="button" className="ott-btn" disabled={page <= 1 || listLoading} onClick={() => setPage((p) => p - 1)}>Previous</button>
                                <button type="button" className="ott-btn" disabled={page >= pages || listLoading} onClick={() => setPage((p) => p + 1)}>Next</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ListOoraTomtomTraffic;